import {
  collection,
  doc,
  getDoc,
  setDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { ShareRole } from '../types';

export interface ShareLink {
  id: string;
  projectId: string;
  ownerId: string;
  projectTitle: string;
  projectUpdatedAt: Date;
  role: ShareRole;
  active: boolean;
  createdAt: Date;
}

export async function createShareLink(
  projectId: string,
  ownerId: string,
  role: ShareRole,
  projectTitle: string,
  projectUpdatedAt: Date
): Promise<ShareLink> {
  const now = Timestamp.now();
  const linkRef = doc(collection(db, 'shareLinks'));
  await setDoc(linkRef, {
    projectId,
    ownerId,
    projectTitle,
    projectUpdatedAt: Timestamp.fromDate(projectUpdatedAt),
    role,
    active: true,
    createdAt: now,
  });

  return {
    id: linkRef.id,
    projectId,
    ownerId,
    projectTitle,
    projectUpdatedAt,
    role,
    active: true,
    createdAt: now.toDate(),
  };
}

export async function getShareLink(shareId: string): Promise<ShareLink | null> {
  const snap = await getDoc(doc(db, 'shareLinks', shareId));
  if (!snap.exists()) return null;
  return {
    id: snap.id,
    ...(snap.data() as Omit<ShareLink, 'id' | 'createdAt'>),
    createdAt: snap.data().createdAt?.toDate?.() ?? new Date(),
    projectUpdatedAt: snap.data().projectUpdatedAt?.toDate?.() ?? new Date(),
  } as ShareLink;
}

export async function acceptShareLink(
  shareId: string,
  userId: string
): Promise<{ projectId: string; role: ShareRole }> {
  const share = await getShareLink(shareId);
  if (!share || !share.active) {
    throw new Error('Share link is invalid or expired');
  }
  const now = Timestamp.now();

  await setDoc(
    doc(db, 'projects', share.projectId, 'collaborators', userId),
    {
      role: share.role,
      addedAt: now,
      addedBy: share.ownerId,
      shareId,
    },
    { merge: true }
  );

  await setDoc(
    doc(db, 'users', userId, 'sharedProjects', share.projectId),
    {
      projectId: share.projectId,
      ownerId: share.ownerId,
      role: share.role,
      title: share.projectTitle || 'Untitled Project',
      addedAt: now,
      updatedAt: Timestamp.fromDate(share.projectUpdatedAt),
      shareId,
    },
    { merge: true }
  );

  return { projectId: share.projectId, role: share.role };
}

export async function getUserProjectRole(
  projectId: string,
  userId: string,
  isAdmin?: boolean,
  isOwner?: boolean
): Promise<ShareRole | 'owner' | null> {
  const projectSnap = await getDoc(doc(db, 'projects', projectId));
  if (!projectSnap.exists()) return null;
  const projectData = projectSnap.data();
  if (projectData.ownerId === userId) return 'owner';

  if (isOwner) return 'edit';

  const collaboratorSnap = await getDoc(
    doc(db, 'projects', projectId, 'collaborators', userId)
  );
  if (collaboratorSnap.exists()) {
    return collaboratorSnap.data().role as ShareRole;
  }

  if (isAdmin) return 'read';

  return null;
}
